import { queryOptions, useQuery } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Field, View, ViewFieldOptions } from '@/api/types'

/**
 * A table's schema and its views.
 *
 * These are server state and behave like it -- read once, invalidated by the
 * realtime layer, shared by every component that needs them -- so they are
 * ordinary queries. The rows are the exception, and the only one; see
 * `features/database/rows/rowWindowStore.ts` for why.
 */

/**
 * Every field on a table, in the order the API returns them.
 *
 * Not ordered here: field *order* is a property of a view, not of the table, and
 * lives in that view's field options. This list is the schema, which the grid,
 * the row modal, the filter editor and the MCP apps all read.
 */
export function fieldsQuery(tableId: number) {
  return queryOptions({
    queryKey: qk.fields(tableId),
    staleTime: Infinity,
    queryFn: (): Promise<Field[]> =>
      unwrap(
        getApi().GET('/api/database/fields/table/{table_id}/', {
          params: { path: { table_id: tableId } },
        }),
      ),
  })
}

export function useFields(tableId: number) {
  return useQuery(fieldsQuery(tableId))
}

/**
 * A table's views, with everything that decides which rows they show.
 *
 * `include` is not optional in practice: without it a view arrives with no
 * filters, sortings or group-bys, and the row query signature built from it
 * would be the same for a filtered view and an unfiltered one.
 *
 * Decorations are in it too, and they are the one include that changes nothing
 * about *which* rows come back -- only what colour they are drawn in. They ride
 * along here rather than in a query of their own because they belong to the
 * view, change when it changes, and are read by the same component that reads
 * everything else about it.
 *
 * The one extra still left out is `default_row_values`, which decides what a
 * new row starts as rather than anything about the ones already there.
 */
export function viewsQuery(tableId: number) {
  return queryOptions({
    queryKey: qk.views(tableId),
    staleTime: Infinity,
    queryFn: (): Promise<View[]> =>
      unwrap(
        getApi().GET('/api/database/views/table/{table_id}/', {
          params: {
            path: { table_id: tableId },
            query: { include: 'filters,sortings,group_bys,decorations' },
          },
        }),
      ),
  })
}

export function useViews(tableId: number) {
  return useQuery(viewsQuery(tableId))
}

/**
 * How one view presents each field: order, width, and whether it is shown.
 *
 * Read from its own endpoint rather than taken off the rows response, which can
 * also carry it. The rows are not a Query cache entry -- they are a window that
 * is thrown away and rebuilt whenever the query changes -- and column widths
 * have no business being discarded because somebody typed in the search box.
 */
export function fieldOptionsQuery(viewId: number) {
  return queryOptions({
    queryKey: qk.fieldOptions(viewId),
    staleTime: Infinity,
    queryFn: (): Promise<ViewFieldOptions> =>
      unwrap(
        getApi().GET('/api/database/views/{view_id}/field-options/', {
          params: { path: { view_id: viewId } },
        }),
      ),
  })
}

export function useFieldOptions(viewId: number) {
  return useQuery(fieldOptionsQuery(viewId))
}
